import React, { useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Controller, Control, RegisterOptions } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { CalendarAPI, UserAPI } from 'api';
import { DeepMap } from 'react-hook-form/dist/types/utils';
import { Role } from 'models/Role';
import {
  Chip,
  makeStyles,
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  FormControlLabel,
} from '@material-ui/core';
import { User } from 'api/userApi';
import UserAvatar from 'components/Users/UserAvatar';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme) => ({
  option: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(0.6),
    paddingBottom: theme.spacing(0.6),
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
  filterChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    maxWidth: '60%',
  },
  selectAllContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    minHeight: 48,
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface Props {
  control: Control;
  name: string;
  rules: RegisterOptions;
  errors: DeepMap<any, any>;
  label: string;
  filterRoles?: Array<Role>;
  className?: string;
  disabled?: boolean;
  shrink?: boolean;
  onChange?: (users: any[]) => void;
  selectedUsers?: User[];
  avoidLimitedUsers?: boolean;
  users?: User[];
  selectedBranches?: string[];
  onRemoveBranch?: (branch: string) => void;
  selectedRoles?: Role[];
  onRemoveRole?: (role: Role) => void;
}

// Helper to get role label
const getRoleLabel = (role: Role) => {
  const roleMap = {
    Administrator: 'Administrator',
    ProjectManager: 'Project Coordinator',
    Supervisor: 'Supervisor',
    Technician: 'Technician',
  };
  return roleMap[role] || role;
};

/** Selects multiple Users (userIds) */
const CalendarMultiUserAutocomplete = ({
  control,
  name,
  rules,
  errors,
  label,
  filterRoles,
  className,
  disabled = false,
  onChange,
  selectedUsers,
  avoidLimitedUsers = false,
  users: usersProp,
  selectedBranches = [],
  onRemoveBranch,
  selectedRoles = [],
  onRemoveRole,
}: Props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { isFetching, data: usersData } = useQuery(
    ['list-users'],
    UserAPI.listUsers,
    { staleTime: 30 * 60 * 1000 }
  );

  const users = usersProp ?? usersData;

  const { data: pinnedUsers, isLoading: isLoadingPinnedUsers } = useQuery(
    ['list-pinned-calendar-users'],
    CalendarAPI.listPinnedCalendarUsers,
    { staleTime: 30 * 60 * 1000 }
  );

  const [open, setOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    if (!users) {
      return;
    }
    if (!filterRoles) {
      if (avoidLimitedUsers) {
        setFilteredUsers(users.filter((u) => !u?.isLimitedUser));
      } else {
        setFilteredUsers(users);
      }
      return;
    }

    if (avoidLimitedUsers) {
      setFilteredUsers(
        users.filter((u) => filterRoles.includes(u.role) && !u?.isLimitedUser)
      );
      return;
    }
    setFilteredUsers(users?.filter((u) => filterRoles.includes(u.role)));
  }, [users, filterRoles, selectedUsers, avoidLimitedUsers]);

  useEffect(() => {
    const nonMatching = users?.filter((user) => {
      return !selectedUsers?.some(
        (selectedUser) => selectedUser.id === user.id
      );
    });
    if (avoidLimitedUsers) {
      setFilteredUsers(nonMatching.filter((u) => !u?.isLimitedUser));
      return;
    } else {
      setFilteredUsers(nonMatching);
    }
  }, [selectedUsers, users, avoidLimitedUsers]);

  const handleSelectAllTechnicians = (props: any) => {
    const technicians =
      users?.filter((u) => u.role === 'Technician' && !u.isLimitedUser) || [];

    if (technicians.length === 0) {
      enqueueSnackbar('No technicians available to select', {
        variant: 'error',
      });
      return;
    }

    onChange(technicians);
    props.onChange(technicians);
    setOpen(false);
  };

  const handleSelectAll = (props: any) => {
    // Remove limited users if avoidLimitedUsers is true
    const usersToSelect = avoidLimitedUsers
      ? users.filter((u) => !u?.isLimitedUser)
      : users;
    
    onChange(usersToSelect);
    props.onChange(usersToSelect);
    setOpen(false);
  };

  if (!isLoadingPinnedUsers) {
    return (
      <Controller
        render={(props) => (
          <Autocomplete
            {...props}
            multiple
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disabled={disabled}
            options={isFetching ? [] : filteredUsers}
            loading={open && isFetching}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            defaultValue={
              pinnedUsers?.map((u) =>
                users?.find((user) => user.id === u.id)
              ) || []
            }
            renderOption={(option) => (
              <span className={classes.option}>
                <UserAvatar
                  user={option}
                  size={24}
                  fontSize={12}
                  className={classes.avatar}
                />
                <Box display="block">
                  <Typography variant="body1">
                    {option.firstName} {option.lastName}
                    {option?.isLimitedUser && (
                      <Chip
                        variant="outlined"
                        style={{ marginLeft: 2 }}
                        size="small"
                        label={
                          <Typography variant="caption">
                            Limited User
                          </Typography>
                        }
                      />
                    )}
                  </Typography>
                  <Typography variant="body2">{option.email}</Typography>
                </Box>
              </span>
            )}
            // Display only first 5 selected users, then "+remaining more"
            renderTags={(value: User[], getTagProps) => {
              const displayCount = 5;
              const visibleUsers = value.slice(0, displayCount);
              const remainingCount = value.length - displayCount;

              return (
                <>
                  {visibleUsers.map((option: User, index: number) => (
                    <Chip
                      key={option.id}
                      variant="outlined"
                      label={`${option.firstName} ${option.lastName}`}
                      avatar={
                        <UserAvatar
                          user={option}
                          size={24}
                          fontSize={12}
                          className={classes.avatar}
                          style={{ color: 'white' }}
                        />
                      }
                      {...getTagProps({ index })}
                    />
                  ))}
                  {remainingCount > 0 && (
                    <Chip
                      variant="outlined"
                      label={`+${remainingCount} more`}
                    />
                  )}
                </>
              );
            }}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                className={className}
                error={!!errors[name]}
                helperText={!!errors[name] && errors[name].message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {open && isFetching && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(_, data) => {
              onChange(data);
              props.onChange(data);
            }}
            PaperComponent={(paperProps) => {
              const { children, ...restPaperProps } = paperProps;
              return (
                <Paper {...restPaperProps}>
                  {filteredUsers?.length > 0 && (
                    <Box
                      onMouseDown={(e) => e.preventDefault()}
                      className={classes.selectAllContainer}>
                      <Box>
                        <FormControlLabel
                          onClick={(e) => e.preventDefault()}
                          label=""
                          control={
                            <Box m={1} className={classes.buttonGroup}>
                              <Button
                                id="select-all-checkbox"
                                variant="outlined"
                                color="secondary"
                                onClick={() => handleSelectAll(props)}>
                                Select all
                              </Button>
                              <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() =>
                                  handleSelectAllTechnicians(props)
                                }>
                                Select all technicians
                              </Button>
                            </Box>
                          }
                        />
                      </Box>
                      <Box className={classes.filterChipsContainer}>
                        {selectedBranches && selectedBranches.length > 0 && (
                          <React.Fragment>
                            {selectedBranches.map((branch) => (
                              <Chip
                                key={branch}
                                label={branch}
                                size="small"
                                onDelete={() => onRemoveBranch?.(branch)}
                                color="primary"
                              />
                            ))}
                          </React.Fragment>
                        )}
                        {selectedRoles && selectedRoles.length > 0 && (
                          <React.Fragment>
                            {selectedRoles.map((role) => (
                              <Chip
                                key={role}
                                label={getRoleLabel(role)}
                                size="small"
                                onDelete={() => onRemoveRole?.(role)}
                                color="secondary"
                              />
                            ))}
                          </React.Fragment>
                        )}
                      </Box>
                    </Box>
                  )}
                  <Divider />
                  {children}
                </Paper>
              );
            }}
          />
        )}
        name={name}
        rules={rules}
        control={control}
        defaultValue={pinnedUsers || []}
      />
    );
  } else {
    return null;
  }
};

export default CalendarMultiUserAutocomplete;
